import React, { useEffect, useState } from "react";
import { useFormikContext } from "formik";
import LinearProgress from "@mui/material/LinearProgress";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import AddIcon from "@mui/icons-material/Add";
import IconButton from "@mui/material/IconButton";
import FormHelperText from "@mui/material/FormHelperText";
import Paper from "@mui/material/Paper";
import Input from "../../../form/Input";
import Autocomplete from "../../../form/Autocomplete";
import ButtonGroup from "../../../form/ButtonGroup";
import Footer from "src/components/wizard/Footer/Footer";
import Image from "../Image";
import Header from "../../../wizard/Header";
import WeightMismatchAlert from "../WeightMismatchAlert";
import UnitsFormField from "./UnitsField/UnitsFormField";
import BillingTypeFormField from "./BillingTypeField/BillingTypeFormField";
import { extractBillingTypes } from "../../../../utils/billingHelpers";
import useMutation from "../../../../hooks/useMutation";
import useNotification from "../../../../hooks/notification";
import { GET_SHIPMENT_QUERY } from "../../../../constants/graphql/queries";
import moment from "moment";
import {
  ADD_SHIPMENT_ATTACHMENT_MUTATION,
  REMOVE_SHIPMENT_ATTACHMENT_MUTATION,
  UPDATE_ATTACHMENT_SHIPMENTS,
} from "../../../../constants/graphql/mutations";
import ScannerInput from "../../../../components/form/ScannerInput";
import SerialNumberField from "../../../../components/form/SerialNumberField";
import ImageUploadButtons from "../../../../views/capture/Images/ImageUploadButtons";
import ReceivingCostCenterModal from "./ReceivingCostCenterModal";
import { compressImageFile } from "src/utils/imageCompression";
import useReceivingWizardFlow from "../hooks/useReceivingWizardFlow";
import IntegrationCounterpartField from "src/components/integrations/IntegrationCounterpartField";

const carrierOptions = ["FedEx", "UPS", "USPS", "Amazon", "DHL"]
  .sort()
  .map(value => ({ value, label: value }));

const AddNewPaper = React.forwardRef(function AddNewPaper(
  { onAddNew, addNewLabel, children, ...paperProps },
  ref
) {
  return (
    <Paper ref={ref} {...paperProps}>
      {children}
      <Divider />
      <Box sx={{ p: 1 }} onMouseDown={e => e.preventDefault()}>
        <Button
          fullWidth
          startIcon={<AddIcon />}
          color="inherit"
          onClick={onAddNew}
          sx={{
            justifyContent: "flex-start",
            textTransform: "none",
            color: "text.secondary",
          }}
        >
          {addNewLabel}
        </Button>
      </Box>
    </Paper>
  );
});

export default function ShipmentFields({
  id,
  shipment,
  project,
  display,
  onClose,
  verticalLayout = false,
  hasIntegration = false,
  loadingIntegration = false,
}) {
  const { isSubmitting, values, errors = {}, submitCount = 0, setFieldValue } = useFormikContext();
  const { notifyError } = useNotification();
  const counterpartProject = project || shipment?.project;
  const activityDate =
    values.receivedAt || shipment?.receivedAt || moment().format("YYYY-MM-DD");
  const [{ loading: loadingCounterpart, hasLink: hasCounterpart }, setCounterpartState] = useState({
    loading: true,
    hasLink: false,
  });
  // Linking is encouraged, never required: a MoveWare outage must not stop
  // receiving. The job can be linked later by reopening this step.

  useEffect(() => {
    if (!hasIntegration) return;
    const nextValue = hasCounterpart ? "linked" : "";
    if (values.movewareDiaryAction !== nextValue) {
      setFieldValue("movewareDiaryAction", nextValue, false);
    }
  }, [hasCounterpart, hasIntegration, setFieldValue, values.movewareDiaryAction]);
  const { totalSteps } = useReceivingWizardFlow(shipment?.project);
  const [isLocked, setIsLocked] = useState(true);
  const [openCostCenterModal, setOpenCostCenterModal] = useState(false);
  const [costCenterAutocompleteKey, setCostCenterAutocompleteKey] = useState(0);
  const [createdCostCenters, setCreatedCostCenters] = useState([]);
  const hasImageError = errors.shipmentImage && submitCount > 0;

  // Check if project has inbound billing types configured
  const inboundBilling = shipment?.project?.billingTypes?.inbound;
  const { types: inboundBillingTypes } = extractBillingTypes(
    inboundBilling,
    shipment?.project?.billingType
  );
  const hasInboundBillingTypes = inboundBillingTypes.length > 0;
  const [addShipmentAttachment, { loading: adding }] = useMutation(
    ADD_SHIPMENT_ATTACHMENT_MUTATION
  );
  const [removeShipmentAttachment, { loading: removing }] = useMutation(
    REMOVE_SHIPMENT_ATTACHMENT_MUTATION
  );
  const [updateAttachmentShipments, { loading: updating }] = useMutation(
    UPDATE_ATTACHMENT_SHIPMENTS
  );

  const activeCostCenterOptions = (
    [...(project?.costCenters || []), ...createdCostCenters]
      ?.filter(v => v.active !== false && !v.disabled)
      .map(v => ({
      value: parseInt(v.id, 10),
      label: `${v.costCenterName || ""}${v.costCenterCode ? ` ${v.costCenterCode}` : ""}`.trim(),
    })) || []
  )
    .filter((option, index, options) => (
      options.findIndex(candidate => candidate.value === option.value) === index
    ))
    .sort((a, b) => a.label.localeCompare(b.label));

  const CostCenterPaperComponent = React.useMemo(
    () =>
      React.forwardRef((props, ref) => (
        <AddNewPaper
          ref={ref}
          {...props}
          onAddNew={() => {
            setCostCenterAutocompleteKey(key => key + 1);
            setOpenCostCenterModal(true);
          }}
          addNewLabel="Add New Cost Center"
        />
      )),
    []
  );

  const handleUpload = async files => {
    if (!files || !files[0]) return;

    const file = await compressImageFile(files[0]);
    addShipmentAttachment({
      variables: { shipmentId: id, file },
      refetchQueries: [{ query: GET_SHIPMENT_QUERY, variables: { id } }],
      onFailure: errors => {
        notifyError(errors);
      },
    });
  };

  const handleRemoveImage = data => {
    removeShipmentAttachment({
      ...data,
      variables: {
        shipmentId: id,
        ...data.variables,
      },
      refetchQueries: [{ query: GET_SHIPMENT_QUERY, variables: { id } }],
    });
  };

  const handleUpdateImage = data => {
    updateAttachmentShipments({
      ...data,
      refetchQueries: [{ query: GET_SHIPMENT_QUERY, variables: { id } }],
    });
  };

  const handleCostCenterCreated = costCenter => {
    setCreatedCostCenters(costCenters => [...costCenters, costCenter]);
    setFieldValue("costCenterId", parseInt(costCenter.id, 10));
    setOpenCostCenterModal(false);
    setCostCenterAutocompleteKey(key => key + 1);
  };

  const toggleLock = () => {
    setIsLocked(!isLocked);
  };

  return (
    <>
      <Header
        title="Shipment Details"
        displayReceivedOn
        isDraft={shipment.draft}
        shipmentStatus={shipment.shipmentStatus}
      />
      <WeightMismatchAlert shipment={shipment} />

      <Box height={3}>{(removing || adding || updating) && <LinearProgress />}</Box>
      <Box
        p={2}
        minHeight={200}
        width="100%"
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        flexGrow={1}
      >
        <Grid container spacing={1}>
          <Grid size={12}>
            <Image
              images={shipment.attachments}
              onUpload={handleUpload}
              onRemove={handleRemoveImage}
              onUpdate={handleUpdateImage}
              helperText="Upload any receiving paperwork here — BOL, packing slips, or manifests"
            />
          </Grid>
          <Grid size={12}>
            <Box my={1} display="flex" alignItems="center" flexDirection="column">
              <Box width="100%" maxWidth={400}>
                <ImageUploadButtons
                  onUpload={e => handleUpload(e.target.files)}
                  hasError={hasImageError}
                  idPrefix="shipment"
                />

                {hasImageError && (
                  <Box mt={0.5} display="flex" justifyContent="center">
                    <FormHelperText error>{errors.shipmentImage}</FormHelperText>
                  </Box>
                )}
              </Box>
            </Box>
          </Grid>
          {hasIntegration && (
            <Grid size={12}>
              <IntegrationCounterpartField
                project={counterpartProject}
                linkableType="Shipment"
                linkableId={shipment?.id}
                date={activityDate}
                onLinkStateChange={setCounterpartState}
              />
            </Grid>
          )}
          {display.shipment_id && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box display="flex" alignItems="center">
                <Box flexGrow={1}>
                  <Input
                    key={isLocked}
                    name="shipmentId"
                    label="Shipment ID"
                    disabled={isLocked}
                    showNAButton={false}
                  />
                </Box>
                <IconButton onClick={toggleLock} size="small" sx={{ ml: 1 }}>
                  {isLocked ? <LockIcon /> : <LockOpenIcon />}
                </IconButton>
              </Box>
            </Grid>
          )}
          {!shipment.draft && hasInboundBillingTypes && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <BillingTypeFormField name="billingType" label="Billing Type" shipment={shipment} />
            </Grid>
          )}
          {display.cost_center && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                key={costCenterAutocompleteKey}
                name="costCenterId"
                label="Cost Center"
                options={activeCostCenterOptions}
                showNAButton={false}
                popperPlacement="bottom-start"
                listboxMaxHeight="20vh"
                PaperComponent={CostCenterPaperComponent}
              />
            </Grid>
          )}
          {display.carrier_name && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                saveCustomOption
                name="carrierName"
                label="Carrier Name"
                options={carrierOptions}
              />
            </Grid>
          )}
          {display.vendor_name && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <Input name="vendorName" label="Vendor Name" />
            </Grid>
          )}
          {display.order_number && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <ScannerInput name="orderNumber" label="Order Number" />
            </Grid>
          )}
          {display.customer_po_number && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <ScannerInput name="customerPoNumber" label="Shipment PO Number" />
            </Grid>
          )}
          {display.bol && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <ScannerInput name="bol" label="BOL Number" />
            </Grid>
          )}
          {display.tracking_number && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <SerialNumberField
                name="trackingNumber"
                label="Tracking Number"
                multiScanLabel="Tracking Number"
              />
            </Grid>
          )}
          {display.truck_number && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <Input name="truckNumber" label="Truck Number" />
            </Grid>
          )}
          {display.total_weight && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <Grid container spacing={1}>
                <Grid size={6}>
                  <Input
                    type="number"
                    name="totalWeight"
                    label="Total Weight"
                    showNAButton={false}
                  />
                </Grid>
                <Grid size={6}>
                  <Box mt="2px">
                    <ButtonGroup
                      name="weightUnits"
                      options={[
                        ["Lbs", "Lbs"],
                        ["Kgs", "Kgs"],
                      ]}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          )}
          {display.total_receiving_units && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <UnitsFormField
                title="Total Handling Units"
                typeLabel="Unit Type"
                quantityLabel="# Received"
                addLabel="Add Another Unit"
                name="totalPieces"
                value={values.totalPieces}
                label="Total Handling Units"
              />
            </Grid>
          )}
          {display.shipment_notes && (
            <Grid size={12}>
              <Input name="notes" label="Optional Notes" type="autoExpand" />
            </Grid>
          )}
        </Grid>
      </Box>
      <Box mt={3}>
        <Footer
          step={0}
          steps={totalSteps}
          startTime={verticalLayout ? null : shipment?.startTime}
          completeTime={verticalLayout ? null : shipment?.completeTime}
        >
          <Button
            disabled={isSubmitting || loadingIntegration || (hasIntegration && loadingCounterpart)}
            startIcon={isSubmitting && <CircularProgress size={22} />}
            type="submit"
            variant="contained"
            fullWidth={verticalLayout}
            sx={{ textTransform: "none" }}
          >
            {verticalLayout ? "Next Step" : "Next"}
          </Button>
        </Footer>
      </Box>
      {openCostCenterModal && (
        <ReceivingCostCenterModal
          open={openCostCenterModal}
          onClose={() => setOpenCostCenterModal(false)}
          projectId={project?.id || shipment?.project?.id}
          onSaved={handleCostCenterCreated}
        />
      )}
    </>
  );
}
